import { IFacetResults, ISearchResultItem } from '@/@types/client-api';
import { IContent } from '@/@types/cms';
import type {
  EditorialSlideBlock,
  FullSizeSlideBlock,
  HomePageCarousel,
  TwoUpSlideBlock
} from '@/@types/content';
import HomeCarousel from '@/components/ui/HomeCarousel/HomeCarousel';
import { getExpiredStatus } from '@/components/ui/PDP/helpers';
import { Component } from '@/lib/ComponentFactory';
import { useGetLibraryUpcomingRegistrationsQuery } from '@/redux/api/client/library';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { addDays } from 'date-fns';
import { BackendDateFormat, formatDate } from '@/utils/helpers';
import useToast from '@/hooks/useToast';
import { delayedActionMessageBuilder } from '@/redux/helpers/delayedActionsMapper';
import { useSearchParamsStable } from '../../../hooks/useSearchParams';
import { LiveNowSlideProps } from './LiveNowSlideBlock';
import { useGetProductByCodeQuery } from '@/redux/api/client/product';
import { isDateOnAir } from '@/utils/dateHelpers';
import { Helmet } from 'react-helmet';
import { useDispatch } from 'react-redux';
import { openCartPanel } from '@/redux/slices/cartSlice';

interface UpcomingProgramItem extends IContent {
  upcomingLiveRegistrationsLimit?: number;
  upcomingDaysLimit?: number;
}

interface DataFilter {
  facetResults?: IFacetResults;
  searchResults?: {
    searchResultsItems?: Record<string, unknown>[];
  };
}

interface CarouselItem extends IContent {
  code?: string;
}

const podcastTypes = ['PodcastEpisodeProduct', 'PodcastEpisodeVariation'];

const liveVariationTypes = [
  'LiveProgramProduct',
  'LiveProgramInteractiveVariation',
  'LiveProgramGroupcastVariation',
  'LiveProgramOhbVariation',
  'LiveProgramInteractiveWebcastVariation',
  'LiveProgramPbrSeminarVariation',
  'LiveProgramPBRWebcastVariation',
  'LiveProgramSeminarVariation',
  'LiveProgramWebcastVariation'
];

const onDemandVariationTypes = ['OnDemandProgramVariation', 'OnDemandProgramProduct'];

const buildProductSlides = (
  carouselItems: IContent[],
  currentLiveNowProgram?: ISearchResultItem[] | undefined | null,
  upcomingPrograms?: Record<string, unknown>[]
) => {
  const filteredCarouselItems = carouselItems.filter(item => {
    const { contentType } = item;

    if (contentType && liveVariationTypes.includes(contentType[1]) && getExpiredStatus(item)) {
      return false;
    }
    return true;
  });

  let items: IContent[] = filteredCarouselItems;

  if (
    upcomingPrograms &&
    carouselItems.find(x => x.contentType?.[1] === 'UpcomingLiveSlideBlock')
  ) {
    const index = carouselItems.findIndex(x => x.contentType?.[1] === 'UpcomingLiveSlideBlock');
    const objects = filteredCarouselItems.filter(
      x => x.contentType?.[1] !== 'UpcomingLiveSlideBlock'
    );

    items = [
      ...objects.slice(0, index),
      ...upcomingPrograms.map(upitem => ({
        ...upitem,
        code: upitem?.productCode,
        sellingPoints: upitem?.summaryText,
        isSummaryTextFromSellingPoints: upitem?.isSummaryTextFromSellingPoints,
        contentType: ['Product', 'LiveProgramProduct']
      })),
      ...objects.slice(index)
    ];
  } else if (
    !upcomingPrograms &&
    carouselItems.find(x => x.contentType?.[1] === 'UpcomingLiveSlideBlock')
  ) {
    items = filteredCarouselItems.filter(x => x.contentType?.[1] !== 'UpcomingLiveSlideBlock');
  }
  items = items.filter(x => x.contentType?.[1] !== 'LiveNowSlideBlock');

  //load liveNow slide if existing
  const liveNowBlocks = carouselItems.filter(x => x.contentType?.[1] === 'LiveNowSlideBlock');
  if (liveNowBlocks && currentLiveNowProgram) {
    const existsProgram = items.find(x => {
      const searchable = x as Record<string, unknown>;
      return (
        x.name == currentLiveNowProgram[0].title ||
        (searchable && searchable['title'] == currentLiveNowProgram[0].title)
      );
    });
    if (!existsProgram) {
      items = items.concat(liveNowBlocks);
    }
  }

  const slides: IContent[] = items.map(item => {
    const { contentType } = item;
    if (contentType && podcastTypes.includes(contentType[1])) {
      return {
        ...item,
        contentType: ['Block', 'PodcastProgramSlide']
      };
    }

    if (contentType && liveVariationTypes.includes(contentType[1])) {
      return {
        ...item,
        contentType: ['Block', 'LiveSliceBlock']
      };
    }

    if (contentType && onDemandVariationTypes.includes(contentType[1])) {
      return {
        ...item,
        contentType: ['Block', 'OnDemandSliceBlock']
      };
    }

    return item;
  });

  return slides;
};

const HomePageCarouselBlock: React.FC<HomePageCarousel> = ({ carouselItems, ...props }) => {
  const dispatch = useDispatch();
  const [slides, setSlides] = useState<IContent[]>(() => buildProductSlides(carouselItems ?? []));
  const { showSuccessMessage } = useToast();
  const { searchParams, setSearchParams } = useSearchParamsStable();
  const [upcomingRegQueryParams, setUpcomingRegQueryParams] = useState<
    { dateRange: string } | undefined
  >(undefined);
  const upcomingRegistrationsConfig: UpcomingProgramItem | undefined = carouselItems?.find(
    i => i.contentType?.[1] === 'UpcomingLiveSlideBlock'
  );

  const {
    data: upcomingProgramsList,
    isFetching,
    isSuccess,
    refetch
  } = useGetLibraryUpcomingRegistrationsQuery(upcomingRegQueryParams, {
    skip: !upcomingRegistrationsConfig || !upcomingRegQueryParams
  });

  const [currentLiveCode, setCurrentLiveCode] = useState('');
  const { data: liveProgramDataByCode } = useGetProductByCodeQuery(currentLiveCode || '');
  const [currentLiveProgram, setCurrentLiveProgram] = useState<
    ISearchResultItem[] | undefined | null
  >(null);

  useEffect(() => {
    if (!upcomingRegistrationsConfig) return;

    const { upcomingDaysLimit } = upcomingRegistrationsConfig;

    const now = new Date();
    const minPossibleNow = addDays(now, -upcomingDaysLimit!);

    const fromDate = formatDate(minPossibleNow, BackendDateFormat);
    const toDate =
      upcomingDaysLimit && upcomingDaysLimit > 0
        ? formatDate(addDays(now, upcomingDaysLimit), BackendDateFormat)
        : null;

    setUpcomingRegQueryParams(toDate ? { dateRange: `${fromDate};${toDate}` } : undefined);
  }, [carouselItems, upcomingRegistrationsConfig]);

  useEffect(() => {
    if (!upcomingRegQueryParams) return;
    refetch();
  }, [upcomingRegQueryParams, refetch]);

  const findAndSetLiveNowProgram = useCallback(() => {
    const liveNowBlocks = carouselItems!.filter(x => x.contentType?.[1] === 'LiveNowSlideBlock');
    if (liveNowBlocks) {
      const now = new Date();
      const nowUtc = new Date(now.toISOString());
      liveNowBlocks.forEach(liveNowBlock => {
        const parentBlock = liveNowBlock as LiveNowSlideProps;
        const programs = parentBlock.liveProgramWebcasts.filter(x =>
          isDateOnAir(new Date(x.eventStartDate!), new Date(x.eventEndDate!), nowUtc)
        );
        if (programs.length > 0) {
          const sortedPrograms = [...programs].sort((a, b) => {
            if (!a.eventStartDate) return 1;
            if (!b.eventStartDate) return -1;

            return new Date(a.eventStartDate).getTime() - new Date(b.eventStartDate).getTime();
          });
          const currentLiveProg = sortedPrograms[0];
          setCurrentLiveCode(currentLiveProg.parentProductCode!.toString());
          if (
            liveProgramDataByCode &&
            liveProgramDataByCode.length > 0 &&
            liveProgramDataByCode[0].isOnAir
          ) {
            setCurrentLiveProgram(liveProgramDataByCode);
          }
        }
      });
    }
  }, [carouselItems, liveProgramDataByCode]);

  const isFinishedEvent = useCallback((item: ISearchResultItem) => {
    const now = new Date();

    const nowUtc = new Date(now.toISOString());

    const startDateTimesUtc = item.locations?.map(x => new Date(x.startTimeUtc!)) as Date[];
    const endDateTimesUtc = item.locations?.map(x => new Date(x.endTimeUtc!)) as Date[];

    const { minDate: minStartDate } = findMinMaxDates(startDateTimesUtc);
    const { maxDate: maxEndDate } = findMinMaxDates(endDateTimesUtc);
    const startDateUtc = minStartDate;
    const endDateUtc = maxEndDate;
    return startDateUtc < nowUtc && endDateUtc < nowUtc;
  }, []);

  const findMinMaxDates = (dates: Date[]) => {
    return dates.reduce(
      (acc, date) => {
        const d = new Date(date);
        acc.minDate = d < acc.minDate ? d : acc.minDate;
        acc.maxDate = d > acc.maxDate ? d : acc.maxDate;
        return acc;
      },
      { minDate: new Date(dates[0]), maxDate: new Date(dates[0]) }
    );
  };

  useEffect(() => {
    if (!carouselItems) return;

    findAndSetLiveNowProgram();
    if (upcomingProgramsList) {
      if (upcomingRegistrationsConfig && (isFetching || !isSuccess)) return;
      const upcomingItems =
        (upcomingProgramsList as unknown as DataFilter)?.searchResults?.searchResultsItems?.filter(
          r =>
            !carouselItems?.some(l => (l as CarouselItem).code === r.variationPk) &&
            !isFinishedEvent(r)
        ) || [];

      const maxUpcoming = Math.min(
        upcomingItems?.length || 0,
        upcomingRegistrationsConfig?.upcomingLiveRegistrationsLimit || 0
      );

      if (carouselItems) {
        setSlides(
          buildProductSlides(carouselItems, currentLiveProgram, upcomingItems.slice(0, maxUpcoming))
        );
      }
    } else {
      setSlides(buildProductSlides(carouselItems, currentLiveProgram));
    }
  }, [
    carouselItems,
    upcomingRegistrationsConfig,
    isFetching,
    upcomingProgramsList,
    isSuccess,
    isFinishedEvent,
    findAndSetLiveNowProgram,
    currentLiveProgram
  ]);

  const logoColors = useMemo(
    () =>
      slides.map(item =>
        'logoColor' in item && item.logoColor === 'Red' ? 'red' : 'white'
      ) as string[],
    [slides]
  );

  const isLiveNowBlock = (component: IContent) => {
    return component.contentType?.[1] === 'LiveNowSlideBlock';
  };

  useEffect(() => {
    if (searchParams.has('successAction')) {
      const delayedAction = searchParams.get('successAction');
      const program = searchParams.get('program');
      let response = null;
      if (searchParams.has('response')) {
        response = JSON.parse(searchParams.get('response') ?? '{}');
      }
      const { cartUrl = null } = response || {};
      if (delayedAction) {
        if (program) {
          if (delayedAction === 'postCartItem') {
            dispatch(openCartPanel());
          } else {
            const { title, url } = JSON.parse(decodeURIComponent(program));
            const successMessage = delayedActionMessageBuilder[delayedAction]?.({
              title,
              url: delayedAction === 'postCartItem' ? cartUrl : url
            });
            showSuccessMessage(successMessage);
          }
        } else {
          if (delayedAction === 'postCartItem') {
            const successMessage = delayedActionMessageBuilder[delayedAction]?.({
              title: 'Added to Cart',
              url: cartUrl
            });
            showSuccessMessage(successMessage);
          }
        }
      }

      setSearchParams(
        existing => {
          existing.delete('successAction');
          existing.delete('program');
          existing.delete('response');
          return existing;
        },
        { replace: true, preventScrollReset: true }
      );
    }
  }, [searchParams, showSuccessMessage, setSearchParams, dispatch]);

  const setPreloadedImages = useMemo(
    () => (
      <Helmet>
        {slides?.map(item => {
          const itemWithImage = item as Pick<
            FullSizeSlideBlock | TwoUpSlideBlock,
            'backGroundImage'
          > &
            Pick<EditorialSlideBlock, 'image'>;
          const imageSrc = itemWithImage?.backGroundImage || itemWithImage?.image;
          return (
            !!imageSrc && (
              <link
                href={imageSrc.url}
                rel="preload"
                as="image"
                type="image/webp"
                key={imageSrc.url}
                fetchPriority="high"
              />
            )
          );
        })}
      </Helmet>
    ),
    [slides]
  );

  return (
    <>
      {setPreloadedImages}
      {!!slides.length && (
        <HomeCarousel {...props} itemLogos={logoColors}>
          {slides?.map((component, idx) => (
            <Component
              key={component.contentLink?.guidValue || idx}
              {...component}
              {...(isLiveNowBlock(component) &&
              liveProgramDataByCode &&
              liveProgramDataByCode.length > 0
                ? {
                    liveProgramProductCode: liveProgramDataByCode[0].productCode,
                    liveProgramVariationPk: liveProgramDataByCode[0].variationPk
                  }
                : {})}
            />
          ))}
        </HomeCarousel>
      )}
    </>
  );
};

export default HomePageCarouselBlock;
